'use client';

import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";


const monthlyPlans = [
  {
    name: "Médico Solo",
    price: "R$197",
    priceSuffix: "/mês",
    patients: "até 15 pacientes/mês",
    users: "1 usuário",
    guarantee: "Garantia Médico-Testada 30 dias",
    isPopular: false,
    features: [
      "Automação pós-operatória",
      "Alertas de risco",
      "Agendamento integrado",
      "Suporte via e-mail",
    ],
  },
  {
    name: "Médico Solo Plus",
    price: "R$297",
    priceSuffix: "/mês",
    patients: "até 40 pacientes/mês",
    users: "1 usuário",
    guarantee: "Garantia Médico-Testada 30 dias",
    isPopular: true,
    features: [
        "Tudo do plano Solo",
        "Protocolos ilimitados",
        "Dashboard avançado",
        "Suporte prioritário",
    ],
  },
  {
    name: "Clínica Essencial",
    price: "R$697",
    priceSuffix: "/mês",
    patients: "até 100 pacientes/mês",
    users: "até 3 médicos",
    guarantee: "Garantia Médico-Testada 30 dias",
    isPopular: false,
    features: [
        "Tudo do plano Plus",
        "Múltiplos usuários",
        "Relatórios de performance",
        "Gerente de conta dedicado",
    ],
  },
];

const annualPlans = [
  {
    name: "Médico Solo",
    price: "R$1.938",
    priceSuffix: "/ano",
    monthlyEquivalent: "R$161,50/mês",
    patients: "até 15 pacientes/mês",
    users: "1 usuário",
    guarantee: "Garantia Médico-Testada 30 dias",
    isPopular: false,
    features: [
        "Automação pós-operatória",
        "Alertas de risco",
        "Agendamento integrado",
        "Suporte via e-mail",
    ],
  },
  {
    name: "Médico Solo Plus",
    price: "R$2.922",
    priceSuffix: "/ano",
    monthlyEquivalent: "R$243,50/mês",
    patients: "até 40 pacientes/mês",
    users: "1 usuário",
    guarantee: "Garantia Médico-Testada 30 dias",
    isPopular: true,
    features: [
        "Tudo do plano Solo",
        "Protocolos ilimitados",
        "Dashboard avançado",
        "Suporte prioritário",
    ],
  },
  {
    name: "Clínica Essencial",
    price: "R$6.857",
    priceSuffix: "/ano",
    monthlyEquivalent: "R$571,40/mês",
    patients: "até 100 pacientes/mês",
    users: "até 3 médicos",
    guarantee: "Garantia Médico-Testada 30 dias",
    isPopular: false,
    features: [
        "Tudo do plano Plus",
        "Múltiplos usuários",
        "Relatórios de performance",
        "Gerente de conta dedicado",
    ],
  },
];

const otherPlans = [
  "Clínica Avançada: pacientes ilimitados, até 6 médicos – R$997/mês (Anual: R$9.813)",
  "Clínica Premium: pacientes ilimitados, até 10 médicos – R$1.497/mês (Anual: R$14.731)",
  "Acima de 10 médicos / White Label: sob consulta"
]

const pricingContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 },
  },
};

const pricingItemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const PlanCard = ({ plan, isAnnual = false, isBot = false }: { plan: any; isAnnual?: boolean, isBot: boolean }) => {
    const Wrapper = isBot ? "div" : motion.div;
    
    return (
        <Wrapper 
            className="w-full"
            variants={isBot ? undefined : pricingItemVariants}
        >
            <Card className={cn("flex flex-col h-full border-border hover:shadow-xl transition-all duration-300 transition-transform hover:-translate-y-2", 
                plan.isPopular ? "bg-secondary border-primary ring-2 ring-primary" : "bg-card"
            )}>
                {plan.isPopular && <div className="bg-primary-foreground text-primary text-center py-1.5 text-sm font-semibold rounded-t-lg">Mais Popular</div>}
                <CardHeader>
                    <CardTitle className="tracking-tight font-semibold">{plan.name}</CardTitle>
                    <CardDescription className="tracking-tight">{plan.patients} • {plan.users}</CardDescription>
                    <div className="pt-4">
                        <span className="text-4xl font-bold">{plan.price}</span>
                        <span className="text-muted-foreground">{plan.priceSuffix}</span>
                    </div>
                    {isAnnual && <p className="text-sm font-medium text-primary">{plan.monthlyEquivalent} (18% de desconto)</p>}
                </CardHeader>
                <CardContent className="flex-1">
                    <ul className="space-y-3">
                        {plan.features.map((feature: string, index: number) => (
                            <li key={index} className="flex items-center gap-2 text-sm tracking-tight">
                                <Check className="h-4 w-4 text-primary"/>
                                <span>{feature}</span>
                            </li>
                        ))}
                    </ul>
                </CardContent>
                <CardFooter className="flex-col items-stretch gap-4">
                    <Button
                      variant="default"
                      className={cn(
                        "w-full",
                        isAnnual && plan.name === 'Médico Solo' && !plan.isPopular && 'bg-[#178595] text-white hover:bg-[#178595]/90',
                        !isAnnual && plan.name === 'Médico Solo' && !plan.isPopular && 'bg-green-300 text-black hover:bg-green-400'
                      )}
                    >
                      {isAnnual && plan.name === "Médico Solo"
                        ? "Assinar plano"
                        : (plan.name === "Médico Solo" ? "Iniciar teste grátis" : "Assinar plano")}
                    </Button>
                    <p className="text-center text-xs text-muted-foreground tracking-tight">{plan.guarantee}</p>
                </CardFooter>
            </Card>
        </Wrapper>
    );
};

type PricingProps = {
    isBot: boolean;
};

export default function Pricing({ isBot }: PricingProps) {
  return (
    <section id="pricing" className="py-10 sm:py-16 bg-primary">
      <div
        className="container mx-auto px-4"
      >
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-headline text-3xl tracking-[-0.04em] leading-[1.1] text-primary-foreground sm:text-4xl font-semibold">
            Planos flexíveis para sua necessidade
          </h2>
          <p className="mt-4 text-lg text-primary-foreground/70 tracking-tight">
            Comece a economizar tempo e otimizar seu atendimento hoje. Cancele a qualquer momento.
          </p>
        </div>

        <Tabs defaultValue="monthly" className="mt-12">
          <TabsList className="grid w-full grid-cols-2 md:w-96 mx-auto">
            <TabsTrigger value="monthly">Assinatura Mensal</TabsTrigger>
            <TabsTrigger value="annual">Assinatura Anual (18% off)</TabsTrigger>
          </TabsList>
          <TabsContent value="monthly" className="mt-10">
              <motion.div
                initial={isBot ? undefined : "hidden"}
                whileInView={isBot ? undefined : "visible"}
                viewport={isBot ? undefined : { once: true, amount: 0.1 }}
                variants={isBot ? undefined : pricingContainerVariants}
                className="grid grid-cols-1 gap-8 lg:grid-cols-3 items-start"
              >
                {monthlyPlans.map((plan) => (
                  <PlanCard key={plan.name} plan={plan} isBot={isBot} />
                ))}
              </motion.div>
          </TabsContent>
          <TabsContent value="annual" className="mt-10">
              <motion.div
                initial={isBot ? undefined : "hidden"}
                whileInView={isBot ? undefined : "visible"}
                viewport={isBot ? undefined : { once: true, amount: 0.1 }}
                variants={isBot ? undefined : pricingContainerVariants}
                className="grid grid-cols-1 gap-8 lg:grid-cols-3 items-start"
              >
                {annualPlans.map((plan) => (
                  <PlanCard key={plan.name} plan={plan} isAnnual isBot={isBot} />
                ))}
              </motion.div>
          </TabsContent>
        </Tabs>

        <div className="mt-16 text-center">
          <h3 className="font-semibold text-lg text-primary-foreground tracking-tight">Precisa de mais?</h3>
          <p className="text-primary-foreground/80 mt-2 tracking-tight">Temos planos para clínicas maiores e soluções White Label.</p>
          <ul className="mt-4 text-sm text-primary-foreground/80 space-y-1 tracking-tight">
            {otherPlans.map((plan, index) => <li key={index}>{plan}</li>)}
          </ul>
          <Button variant="link" asChild className="mt-4 text-primary-foreground/90 hover:text-primary-foreground">
            <Link href="#">Entre em contato para um plano personalizado</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
